/**
 * Branding por cuenta para el frontend.
 *
 * Espejo de `backend/src/utils/branding.ts` (el backoffice): mapa keyeado por
 * email en minusculas, en codigo y no en base de datos.
 *
 * La diferencia con el backoffice es a quien le aplica. Aca el branding se
 * hereda: una cuenta madre lo define y todos los usuarios creados desde ella
 * (hasta 3 niveles, que es la profundidad que maneja la API) lo reciben. Eso
 * se resuelve con `user.accountChain`, la lista de emails de la cuenta y sus
 * ancestros que devuelve la query `accountChain` de la API.
 */

export type Branding = {
  /** Slug del login de agencias: `?brand=<slug>`. Espeja al del backoffice. */
  slug?: string;
  color: string;
  logo?: string;
  logoMobile?: string;
  footerLogo?: string;
};

/** Usuario tal como vive en el store (`StateUser`), en su forma minima. */
export type BrandableUser = {
  email?: string;
  accountChain?: Array<string>;
};

/** El `--blue` historico de Cardinal (`src/App.vue` :root). */
export const DEFAULT_BRAND_COLOR = "#0055b8";

/** Los archivos viven en `public/`, se resuelven en runtime (sin require). */
const publicAsset = (file: string): string =>
  `${process.env.BASE_URL || "/"}${file}`;

/**
 * Cuentas madre con marca propia. La clave es el email de la cuenta que
 * define la marca; sus usuarios hijos la heredan via `accountChain`.
 */
export const ACCOUNT_BRANDING: Record<string, Branding> = {
  // Cuenta de prueba, con la misma marca que grupo8.
  "roman.vazquez@cardinalsisa.com": {
    slug: "test",
    color: "#dc2c34",
    logo: publicAsset("logo-branded.svg"),
    logoMobile: publicAsset("logo-branded.svg"),
    footerLogo: publicAsset("logo-branded.svg")
  },
  "operaciones@grupo8.com.ar": {
    slug: "grupo8",
    color: "#dc2c34",
    logo: publicAsset("logo-branded.svg"),
    logoMobile: publicAsset("logo-branded.svg"),
    footerLogo: publicAsset("logo-branded.svg")
  }
};

export const isValidHexColor = (value: string): boolean =>
  /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value);

/** Lookup directo por email, sin herencia. */
export const getBranding = (email?: string): Branding | undefined =>
  email ? ACCOUNT_BRANDING[email.toLowerCase()] : undefined;

/**
 * Branding efectivo de un usuario. Recorre su cadena de cuentas de la mas
 * cercana a la mas lejana y se queda con la primera que tenga marca propia,
 * asi una cuenta madre branded le gana a un ancestro sin marca.
 *
 * Si todavia no llego `accountChain` desde la API, cae al email propio: la
 * cuenta madre sigue viendo su marca aunque los hijos no la vean hasta que
 * la query responda.
 */
export const getUserBranding = (
  user?: BrandableUser
): Branding | undefined => {
  if (!user) return undefined;
  const chain =
    user.accountChain && user.accountChain.length
      ? user.accountChain
      : user.email
      ? [user.email]
      : [];
  for (const email of chain) {
    const branding = getBranding(email);
    if (branding) return branding;
  }
  return undefined;
};

/** True si el usuario tiene marca propia (propia o heredada). */
export const hasBranding = (user?: BrandableUser): boolean =>
  !!getUserBranding(user);

/**
 * Oscurece un hex mezclandolo hacia negro. Se usa para derivar la
 * superficie del panel de filtros del color de marca: con `--blue` rojo,
 * el sidebar tiene que acompanar y no quedarse en el navy de Cardinal.
 */
export const shade = (hex: string, amount: number): string => {
  if (!isValidHexColor(hex)) return hex;
  let h = hex.slice(1);
  if (h.length === 3)
    h = h
      .split("")
      .map(c => c + c)
      .join("");
  const keep = Math.max(0, Math.min(1, 1 - amount));
  const ch = (i: number) =>
    Math.round(parseInt(h.slice(i, i + 2), 16) * keep)
      .toString(16)
      .padStart(2, "0");
  return `#${ch(0)}${ch(2)}${ch(4)}`;
};

/**
 * Cuanto se oscurece el color de marca para el panel de filtros del
 * listado. El valor por defecto sin marca vive en `:root` (`--filter-bg`).
 */
export const FILTER_BG_SHADE = 0.42;

/**
 * Pisa `--blue` en :root. Como toda la hoja de estilos usa `var(--blue)`,
 * con esto alcanza para retenir la app entera. La clase `branded` habilita
 * las reglas que solo aplican a cuentas con marca propia.
 *
 * Ademas deriva `--filter-bg` (panel de filtros del listado, anillo de
 * hover de las cards, CTA del mapa) del color de marca: es una superficie
 * oscura y no puede quedarse en el navy de Cardinal cuando la marca es de
 * otro color. Sin marca no se toca y vale el default de `:root`.
 */
export const applyBrandColor = (color: string): void => {
  if (isValidHexColor(color)) {
    document.documentElement.style.setProperty("--blue", color);
    document.documentElement.style.setProperty(
      "--filter-bg",
      shade(color, FILTER_BG_SHADE)
    );
    document.documentElement.classList.add("branded");
  }
};

export const resetBrandColor = (): void => {
  document.documentElement.style.removeProperty("--blue");
  document.documentElement.style.removeProperty("--filter-bg");
  document.documentElement.classList.remove("branded");
};

/** Aplica o limpia el color segun el usuario logueado. */
export const applyBrandingForUser = (user?: BrandableUser): void => {
  const branding = getUserBranding(user);
  if (branding) applyBrandColor(branding.color);
  else resetBrandColor();
};
